<script setup lang="ts">
import { Form, Field } from 'vee-validate'
import * as yup from 'yup'
import { useAllStores } from '@/store'
import { useMyFetch } from '@/utils'
import { handleError } from '@/utils/errors'
import { ref } from 'vue'

const { organizationStore, alertStore, authStore, router, t } = useAllStores()
const unix_username = ref<string>('')

const schema_username: yup.StringSchema = yup
  .string()
  .required(t('required_username'))
  .matches(/^[a-z_]([a-z0-9_-]{0,31}|[a-z0-9_-]{0,30}\$)$/, t('invalid_username'))

const candidateUsername = authStore.user.email.split('@')[0].replace(/[^a-z0-9_-]/g, '')
schema_username.validate(candidateUsername).then((valid) => {
  if (valid) {
    unix_username.value = candidateUsername
  }
})

const schema = yup.object({
  name: yup.string().required(t('required_org')),
  unix_username: schema_username
})

async function onSubmit(values: any) {
  const { name, unix_username } = values
  const { data, error } = await useMyFetch('organizations').post({ name, unix_username }).json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    alertStore.success('Organization created')
    organizationStore.selectOrganization(data.value.id, data.value.name)
    await router.push({ name: 'organizations:list' })
  }
}
</script>

<i18n lang="yaml">
en:
  register_org: Register an organization
  card_text1: Register an organization so you can add resources and manage them.
  card_text2: You will be the owner of the organization and will be able to invite your team members.
  name: Organization name
  unix_username: UNIX username
  required_org: Organization name is required
  required_username: UNIX username is required
  invalid_username: Username must start with a letter or underscore and contain only letters, numbers, hyphens, and underscores
  create: Create
ru:
  register_org: Зарегистрировать организацию
  card_text1: Зарегистрируйте организацию, чтобы добавлять ресурсы и управлять ими.
  card_text2: Вы будете владельцем организации и сможете приглашать участников.
  name: Название организации
  unix_username: UNIX имя пользователя
  required_org: Название организации обязательно
  required_username: UNIX имя пользователя обязательно
  invalid_username: Имя пользователя должно начинаться с буквы или подчеркивания и содержать только буквы, цифры, дефисы и подчеркивания
  create: Создать
</i18n>

<template>
  <div class="card">
    <h4 class="card-header">{{ t('register_org') }}</h4>
    <div class="card-body">
      <p class="card-text">{{ t('card_text1') }}</p>
      <p class="card-text">{{ t('card_text2') }}</p>
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
        <div class="form-group mb-3">
          <label>{{ t('name') }}</label>
          <Field
            v-focus
            name="name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
          />
          <div class="invalid-feedback">{{ errors.name }}</div>
        </div>
        <div class="form-group mb-3">
          <label>{{ t('unix_username') }}</label>
          <Field
            v-model="unix_username"
            name="unix_username"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.unix_username }"
          />
          <div class="invalid-feedback">{{ errors.unix_username }}</div>
        </div>
        <div class="form-group mb-3">
          <button class="btn btn-primary" :disabled="isSubmitting">
            <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span
            >{{ t('create') }}
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>
